import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Camera, CheckCircle2, DollarSign, Loader2, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 4;

const CONDITIONS = ["Runs & drives", "Doesn't run", "Wrecked / damaged", "Scrap only", "Old equipment / machine"];

const initial = { name: "", phone: "", vehicle: "", condition: CONDITIONS[0], location: "", notes: "" };

export default function JunkCarForm() {
  const [form, setForm] = useState(initial);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPhotos((p) => [...p, ...files].slice(0, MAX_PHOTOS));
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/junk-car-requests`, form);
      if (photos.length) {
        const fd = new FormData();
        photos.forEach((p) => fd.append("files", p));
        await axios.post(`${API}/junk-car-requests/${data.id}/photos`, fd);
      }
      setDone(data);
      toast.success("Quote request sent — we'll call with an offer.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send request. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-ink px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-200 focus:border-ember";

  return (
    <section id="sell" data-testid="junk-car-section" className="bg-ink py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember" data-testid="junk-overline">
              Cash for Clunkers
            </p>
            <h2 className="mt-4 font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-6xl" data-testid="junk-heading">
              Sell Your<br />Junk Car
            </h2>
            <p className="mt-6 max-w-md text-base text-slate-400">
              That dead car, rusty truck, or old machine sitting in the yard? We buy it — fair price,
              and we haul it away ourselves. Tell us what you've got and we'll call with an offer.
            </p>
            <div className="mt-10 space-y-3 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
              <p className="flex items-center gap-2" data-testid="junk-note-cash"><DollarSign className="h-4 w-4 text-ember" /> Fair cash offers</p>
              <p className="flex items-center gap-2" data-testid="junk-note-haul"><DollarSign className="h-4 w-4 text-ember" /> Free haul-away with the deal</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            {done ? (
              <div data-testid="junk-success-panel" className="flex h-full flex-col items-start justify-center border border-ember/40 bg-surface p-10">
                <CheckCircle2 className="h-12 w-12 text-ember" />
                <h3 className="mt-6 font-display text-3xl font-extrabold uppercase text-white">Quote Request Sent</h3>
                <p className="mt-3 text-sm text-slate-400">
                  Reference <span className="font-mono text-ember" data-testid="junk-reference-id">{done.id.slice(0, 8).toUpperCase()}</span> — we'll call <span className="text-white">{done.phone}</span> with an offer.
                </p>
                <button
                  onClick={() => { setDone(null); setForm(initial); setPhotos([]); }}
                  data-testid="junk-new-button"
                  className="mt-8 border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-widest text-white transition-colors duration-200 hover:border-ember hover:text-ember"
                >
                  Sell another vehicle
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="junk-car-form" className="grid grid-cols-1 gap-5 border border-white/10 bg-surface p-6 sm:grid-cols-2 sm:p-10">
                <div>
                  <label htmlFor="junk-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Your Name</label>
                  <input id="junk-name" data-testid="junk-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="John Miller" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="junk-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Callback Phone</label>
                  <input id="junk-phone" data-testid="junk-phone-input" required minLength={7} type="tel" value={form.phone} onChange={set("phone")} placeholder="(330) 555-0123" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="junk-vehicle" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Vehicle / Machine</label>
                  <input id="junk-vehicle" data-testid="junk-vehicle-input" required minLength={3} value={form.vehicle} onChange={set("vehicle")} placeholder="2004 Ford F-150, old tractor…" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="junk-condition" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Condition</label>
                  <select id="junk-condition" data-testid="junk-condition-select" value={form.condition} onChange={set("condition")} className={inputCls}>
                    {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="junk-location" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Where Is It?</label>
                  <input id="junk-location" data-testid="junk-location-input" required minLength={3} value={form.location} onChange={set("location")} placeholder="Street address or nearest town" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="junk-notes" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Anything Else? <span className="text-slate-600">(optional)</span></label>
                  <input id="junk-notes" data-testid="junk-notes-input" value={form.notes} onChange={set("notes")} placeholder="Has title, missing engine, two of them…" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                    Photos <span className="text-slate-600">(optional, up to {MAX_PHOTOS})</span>
                  </label>
                  <input id="junk-photos" data-testid="junk-photo-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={addPhotos} className="hidden" />
                  <label htmlFor="junk-photos" data-testid="junk-photo-picker" className="flex cursor-pointer items-center gap-3 border border-dashed border-white/20 bg-ink px-4 py-3.5 text-sm text-slate-400 transition-colors duration-200 hover:border-ember hover:text-ember">
                    <Camera className="h-4 w-4" />
                    {photos.length ? `Add more photos (${photos.length}/${MAX_PHOTOS} attached)` : "Attach photos — gets you a faster, better offer"}
                  </label>
                  {photos.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3" data-testid="junk-photo-previews">
                      {photos.map((p, i) => (
                        <div key={i} className="relative h-20 w-20 overflow-hidden border border-white/15">
                          <img src={URL.createObjectURL(p)} alt={p.name} className="h-full w-full object-cover" data-testid={`junk-photo-preview-${i}`} />
                          <button type="button" onClick={() => setPhotos((ps) => ps.filter((_, idx) => idx !== i))} data-testid={`junk-photo-remove-${i}`} className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center bg-ink/90 text-white hover:bg-red-600">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <button type="submit" disabled={loading} data-testid="junk-submit-button" className="flex w-full items-center justify-center gap-3 bg-ember px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-white transition-all duration-200 hover:bg-emberdark hover:shadow-[0_0_36px_rgba(139,92,246,0.35)] disabled:opacity-60">
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                    {loading ? "Sending…" : "Get My Offer"}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
